// src/components/tabs/AssemblyTab.jsx
// Contract Assembly — replaces ExhibitsTab
// Full contract structure: STD215 → STD213 → Exhibits (with nested attachments) → General Attachments → Export

import { useState, useEffect, useRef } from 'react';
import { Icons, EmptyState } from '../UI';
import { uid, today } from '../../utils/constants';
import supabase from '../../utils/supabaseClient';

// ── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ source }) {
  const config = {
    penny_draft:    { label: 'Penny Draft',    bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    user_uploaded:  { label: 'User Uploaded',  bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
    legal_revised:  { label: 'Legal Revised',  bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff' },
    exec_approved:  { label: 'Exec Approved',  bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
    empty:          { label: 'Not Started',    bg: '#f8fafc', color: '#94a3b8', border: '#e2e8f0' },
  }[source || 'empty'] || { label: source, bg: '#f8fafc', color: '#94a3b8', border: '#e2e8f0' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: config.bg, color: config.color, border: '1px solid ' + config.border }}>
      {config.label}
    </span>
  );
}

// ── PDF page counter — reads page count from base64 PDF ─────────────────────
function countPdfPages(base64) {
  try {
    const binary = atob(base64);
    let count = 0;
    const needle = '/Type /Page';
    const needle2 = '/Type/Page';
    for (let i = 0; i < binary.length - 20; i++) {
      const s11 = binary.slice(i, i + 11);
      const s10 = binary.slice(i, i + 10);
      const matchFull = (s11 === needle || s10 === needle2);
      if (matchFull) {
        const next = binary.slice(i + 10, i + 12);
        if (!next.startsWith('s')) count++;
      }
    }
    return count > 0 ? count : null;
  } catch (e) { return null; }
}

// ── Attachment row ────────────────────────────────────────────────────────────
function AttachmentRow({ att, index, onUpdate, onRemove, onUpload }) {
  const fileRef = useRef(null);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f8fafc', borderRadius: 6, marginBottom: 4, border: '1px solid #e2e8f0' }}>
      <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, minWidth: 80 }}>Attachment {index + 1}</span>
      <input
        value={att.name || ''}
        onChange={e => onUpdate({ ...att, name: e.target.value })}
        placeholder="Attachment name"
        style={{ flex: 1, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 5, fontSize: 12, fontFamily: 'inherit' }}
      />
      <input
        value={att.pages || ''}
        onChange={e => onUpdate({ ...att, pages: e.target.value })}
        placeholder="pp"
        type="number"
        style={{ width: 44, padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: 5, fontSize: 11, fontFamily: 'inherit', textAlign: 'center', color: '#94a3b8' }}
      />
      <StatusBadge source={att.source || 'empty'} />
      <button onClick={() => fileRef.current?.click()} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 5, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#64748b' }}>
        ↑ Upload
      </button>
      <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }}
        onChange={e => e.target.files[0] && onUpload(e.target.files[0], att.id)} />
      <button onClick={() => onRemove(att.id)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14, padding: '0 2px' }}>✕</button>
    </div>
  );
}

// ── Exhibit card ──────────────────────────────────────────────────────────────
function ExhibitCard({ ex, onUpdate, onRemove, onAddAttachment, onUpdateAttachment, onRemoveAttachment, onUploadAttachment, onPullFromPenny, onSyncToPenny, pennyDrafts, isFirst, isLast, onMoveUp, onMoveDown }) {
  const [expanded, setExpanded] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const pennyFinal = pennyDrafts?.['ex' + ex.label + 'Final'];
  const hasFinal = !!(pennyFinal?.source || ex.finalVersion?.source);
  const activeFinal = pennyFinal || ex.finalVersion || null;
  const isLocked = ex.locked || ex.referenceOnly;

  async function handleUploadFinal(file) {
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        const pageCount = countPdfPages(base64);
        const finalObj = {
          source: 'user_uploaded',
          uploadedAt: new Date().toISOString(),
          fileName: file.name,
          size: file.size,
          base64,
        };
        // Update exhibit with page count
        onUpdate(ex.id, {
          ...ex,
          pages: pageCount ? String(pageCount) : ex.pages,
          finalVersion: finalObj,
        });
        // Sync back to pennyDrafts so Penny tab stays in sync
        onSyncToPenny('ex' + ex.label + 'Final', finalObj);
        toast.success(file.name + ' uploaded' + (pageCount ? ' — ' + pageCount + ' pages' : '') + ' · Penny tab synced ✓');
      };
      reader.readAsDataURL(file);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{
      background: '#fff', borderRadius: 12,
      border: '1.5px solid ' + (hasFinal ? '#bbf7d0' : isLocked ? '#e2e8f0' : '#e2e8f0'),
      marginBottom: 10, overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,.04)',
    }}>
      {/* Card header */}
      <div style={{
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
        background: isLocked ? '#f8fafc' : hasFinal ? '#f0fdf4' : '#fff',
        borderBottom: expanded ? '1px solid #f1f5f9' : 'none',
        cursor: 'pointer',
      }} onClick={() => !isLocked && setExpanded(e => !e)}>

        {/* Reorder buttons */}
        {!isLocked && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <button onClick={e => { e.stopPropagation(); onMoveUp(); }} disabled={isFirst}
              style={{ background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer', color: isFirst ? '#e2e8f0' : '#94a3b8', fontSize: 10, lineHeight: 1, padding: '1px 3px' }}>▲</button>
            <button onClick={e => { e.stopPropagation(); onMoveDown(); }} disabled={isLast}
              style={{ background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer', color: isLast ? '#e2e8f0' : '#94a3b8', fontSize: 10, lineHeight: 1, padding: '1px 3px' }}>▼</button>
          </div>
        )}

        {/* Exhibit label */}
        <div style={{ width: 32, height: 32, borderRadius: 8, background: isLocked ? '#f1f5f9' : '#0f1f3d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: isLocked ? '#94a3b8' : '#e8c04a', flexShrink: 0 }}>
          {ex.label}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f1f3d' }}>
              Exhibit {ex.label} — {ex.name || '[Unnamed]'}
            </div>
            {isLocked && <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>REFERENCE ONLY</span>}
            {hasFinal && <StatusBadge source={activeFinal?.source} />}
          </div>
          {ex.referenceOnly && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
              {ex.gtcVersion || 'GTC 04/2017'} · State of California · Public document
            </div>
          )}
          {ex.pages && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{ex.pages} pages</div>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {hasFinal && <span style={{ fontSize: 16 }}>✅</span>}
          {!isLocked && <span style={{ fontSize: 12, color: '#94a3b8' }}>{expanded ? '▲' : '▼'}</span>}
          {!isLocked && (
            <button onClick={e => { e.stopPropagation(); onRemove(ex.id); }}
              style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14, padding: '0 2px' }}>✕</button>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && !isLocked && (
        <div style={{ padding: '14px 16px' }}>

          {/* Exhibit name + pages */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, marginBottom: 12 }}>
            <input
              value={ex.name || ''}
              onChange={e => onUpdate(ex.id, { ...ex, name: e.target.value })}
              placeholder="Exhibit name"
              style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontFamily: 'inherit' }}
            />
            <input
              type="number" value={ex.pages || ''}
              onChange={e => onUpdate(ex.id, { ...ex, pages: e.target.value })}
              placeholder="Pages"
              style={{ width: 70, padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, textAlign: 'center', fontFamily: 'monospace' }}
            />
          </div>

          {/* Final Version zone */}
          <div style={{ padding: '12px 14px', borderRadius: 9, background: hasFinal ? '#f0fdf4' : '#f8fafc', border: '1.5px solid ' + (hasFinal ? '#bbf7d0' : '#e2e8f0'), marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: hasFinal ? '#16a34a' : '#64748b', letterSpacing: '.04em', marginBottom: 8 }}>
              {hasFinal ? '✅ FINAL VERSION' : '📄 FINAL VERSION — Not set'}
            </div>
            {hasFinal ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <StatusBadge source={activeFinal?.source} />
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    {activeFinal?.fileName || 'Penny Draft'} · {activeFinal?.uploadedAt ? new Date(activeFinal.uploadedAt).toLocaleDateString() : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => fileRef.current?.click()}
                    style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#374151' }}>
                    ↑ Replace Final
                  </button>
                  <button onClick={() => onUpdate(ex.id, { ...ex, finalVersion: null })}
                    style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#dc2626' }}>
                    ✕ Clear Final
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {pennyDrafts?.sow && ex.label === 'A' && (
                  <button onClick={() => onPullFromPenny(ex.id, 'sow')}
                    style={{ padding: '6px 12px', borderRadius: 7, border: '1.5px solid #c49922', background: '#fffbeb', color: '#92400e', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    ✒ Use Penny Draft
                  </button>
                )}
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  style={{ padding: '6px 12px', borderRadius: 7, border: '1.5px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {uploading ? 'Uploading…' : '↑ Upload Final Version'}
                </button>
              </div>
            )}
            <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }}
              onChange={e => e.target.files[0] && handleUploadFinal(e.target.files[0])} />
          </div>

          {/* Exhibit-specific attachments */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '.04em' }}>
                EXHIBIT ATTACHMENTS
              </div>
              <button onClick={() => onAddAttachment(ex.id)}
                style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 11, cursor: 'pointer', color: '#374151' }}>
                + Add Attachment
              </button>
            </div>
            {(ex.attachments || []).length === 0 ? (
              <div style={{ fontSize: 12, color: '#cbd5e1', fontStyle: 'italic', padding: '4px 0' }}>No attachments for this exhibit</div>
            ) : (
              (ex.attachments || []).map((att, i) => (
                <AttachmentRow
                  key={att.id} att={att} index={i}
                  onUpdate={updated => onUpdateAttachment(ex.id, att.id, updated)}
                  onRemove={() => onRemoveAttachment(ex.id, att.id)}
                  onUpload={(file, attId) => onUploadAttachment(ex.id, attId, file)}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Reference only content (Exhibit C) */}
      {expanded && ex.referenceOnly && (
        <div style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: '#64748b' }}>GTC Version:</span>
            <input
              value={ex.gtcVersion || 'GTC 04/2017'}
              onChange={e => onUpdate(ex.id, { ...ex, gtcVersion: e.target.value })}
              style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 5, fontSize: 12, fontFamily: 'monospace', width: 120 }}
            />
            <span style={{ fontSize: 11, color: '#94a3b8' }}>Available at dgs.ca.gov</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main AssemblyTab ──────────────────────────────────────────────────────────
export default function AssemblyTab({ contract, updateC, profile, pennyDrafts, toast, apiKey, setTab, s215 }) {
  const exhibits = contract.exhibits || [];
  const generalAttachments = contract.general_attachments || [];

  // Auto page count — fill missing page counts from pennyDrafts finals
  useEffect(() => {
    const toUpdate = [];
    for (const ex of exhibits) {
      if (ex.referenceOnly) continue;
      if (ex.pages) continue; // already has pages
      const pFinal = pennyDrafts?.['ex' + ex.label + 'Final'];
      if (pFinal?.base64) {
        const count = countPdfPages(pFinal.base64);
        if (count) toUpdate.push({ id: ex.id, pages: String(count) });
      }
    }
    if (toUpdate.length > 0) {
      updateC({
        exhibits: exhibits.map(e => {
          const upd = toUpdate.find(u => u.id === e.id);
          return upd ? { ...e, pages: upd.pages } : e;
        })
      });
    }
  }, [pennyDrafts]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const generalFileRef = useRef(null);

  // ── Exhibit mutations ────────────────────────────────────────────────
  function addExhibit() {
    const labels = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const usedLabels = exhibits.filter(e => !e.referenceOnly).map(e => e.label);
    const nextLabel = labels.split('').find(l => !usedLabels.includes(l)) || 'X';
    const newEx = {
      id: uid(), label: nextLabel, name: '', pages: '',
      source: 'empty', pennyReview: true, locked: false, referenceOnly: false,
      attachments: [], finalVersion: null, versionHistory: [],
    };
    updateC({ exhibits: [...exhibits, newEx] });
  }

  function initializeDefaults() {
    const gtcVersion = profile?.agencyGtcVersion || 'GTC 04/2017';
    const defaults = [
      { id: uid(), label: 'A', name: 'Statement of Work', pages: '', source: 'empty', pennyReview: true, locked: false, referenceOnly: false, attachments: [], finalVersion: null, versionHistory: [] },
      { id: uid(), label: 'B', name: 'Budget Detail and Payment Provisions', pages: '', source: 'empty', pennyReview: true, locked: false, referenceOnly: false, attachments: [], finalVersion: null, versionHistory: [] },
      { id: uid(), label: 'C', name: 'General Terms and Conditions', pages: '', source: 'reference', pennyReview: false, locked: true, referenceOnly: true, gtcVersion, attachments: [], finalVersion: null, versionHistory: [] },
      { id: uid(), label: 'D', name: 'Special Terms and Conditions', pages: '', source: 'empty', pennyReview: true, locked: false, referenceOnly: false, attachments: [], finalVersion: null, versionHistory: [] },
    ];
    updateC({ exhibits: defaults });
    toast.success('Standard exhibits initialized — A, B, C, D');
  }

  function updateExhibit(id, updated) {
    updateC({ exhibits: exhibits.map(e => e.id === id ? updated : e) });
  }

  function removeExhibit(id) {
    updateC({ exhibits: exhibits.filter(e => e.id !== id) });
  }

  function moveExhibit(idx, dir) {
    const arr = [...exhibits];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    updateC({ exhibits: arr });
  }

  // ── Attachment mutations ──────────────────────────────────────────────
  function addAttachment(exhibitId) {
    updateC({
      exhibits: exhibits.map(e => e.id === exhibitId
        ? { ...e, attachments: [...(e.attachments || []), { id: uid(), name: '', source: 'empty', pages: '' }] }
        : e
      )
    });
  }

  function updateAttachment(exhibitId, attId, updated) {
    updateC({
      exhibits: exhibits.map(e => e.id === exhibitId
        ? { ...e, attachments: (e.attachments || []).map(a => a.id === attId ? updated : a) }
        : e
      )
    });
  }

  function removeAttachment(exhibitId, attId) {
    updateC({
      exhibits: exhibits.map(e => e.id === exhibitId
        ? { ...e, attachments: (e.attachments || []).filter(a => a.id !== attId) }
        : e
      )
    });
  }

  async function uploadAttachment(exhibitId, attId, file) {
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];
      let storagePath = null;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user && contract?.id) {
          const path = user.id + '/' + contract.id + '/att_' + attId + '_' + file.name.replace(/\s+/g, '_');
          const bytes = atob(base64);
          const arr = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
          const blob = new Blob([arr], { type: 'application/pdf' });
          const { error } = await supabase.storage.from('exhibit-files').upload(path, blob, { upsert: true });
          if (!error) storagePath = path;
        }
      } catch (e) { console.warn('Attachment storage upload failed:', e.message); }
      const pageCount = countPdfPages(base64);
      updateAttachment(exhibitId, attId, {
        id: attId, name: file.name.replace(/\.pdf$/i, ''),
        source: 'user_uploaded', size: file.size,
        pages: pageCount ? String(pageCount) : '',
        storagePath, base64: storagePath ? null : base64,
        uploadedAt: new Date().toISOString(),
      });
      toast.success(file.name + ' uploaded' + (pageCount ? ' — ' + pageCount + ' pages' : ''));
    };
    reader.readAsDataURL(file);
  }

  // ── General attachments ───────────────────────────────────────────────
  function addGeneralAttachment() {
    updateC({ general_attachments: [...generalAttachments, { id: uid(), name: '', source: 'empty' }] });
  }

  function updateGeneralAttachment(id, updated) {
    updateC({ general_attachments: generalAttachments.map(a => a.id === id ? updated : a) });
  }

  function removeGeneralAttachment(id) {
    updateC({ general_attachments: generalAttachments.filter(a => a.id !== id) });
  }

  async function uploadGeneralAttachment(file) {
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];
      const attId = uid();
      let storagePath = null;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user && contract?.id) {
          const path = user.id + '/' + contract.id + '/gatt_' + attId + '_' + file.name.replace(/\s+/g, '_');
          const bytes = atob(base64);
          const arr = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
          const blob = new Blob([arr], { type: 'application/pdf' });
          const { error } = await supabase.storage.from('exhibit-files').upload(path, blob, { upsert: true });
          if (!error) storagePath = path;
        }
      } catch (e) { console.warn('General attachment storage upload failed:', e.message); }
      const pageCount = countPdfPages(base64);
      const newAtt = {
        id: attId, name: file.name.replace(/\.pdf$/i, ''),
        source: 'user_uploaded', size: file.size,
        pages: pageCount ? String(pageCount) : '',
        storagePath, base64: storagePath ? null : base64,
        uploadedAt: new Date().toISOString(),
      };
      updateC({ general_attachments: [...generalAttachments, newAtt] });
      toast.success(file.name + ' added' + (pageCount ? ' — ' + pageCount + ' pages' : ''));
    };
    reader.readAsDataURL(file);
  }

  // ── Pull from Penny ───────────────────────────────────────────────────
  function syncToPenny(key, value) {
    updateC({ pennyDrafts: { [key]: value } });
  }

  function pullFromPenny(exhibitId, type) {
    const content = pennyDrafts?.[type];
    if (!content) { toast.warn('No Penny draft found — generate one in the Penny tab first'); return; }
    updateC({
      exhibits: exhibits.map(e => e.id === exhibitId
        ? { ...e, finalVersion: { source: 'penny_draft', content, uploadedAt: new Date().toISOString() } }
        : e
      )
    });
    toast.success('Penny draft set as Final Version for Exhibit ' + exhibits.find(e => e.id === exhibitId)?.label);
  }

  // ── Total pages ───────────────────────────────────────────────────────
  const totalPages = exhibits.reduce((s, e) => s + (parseInt(e.pages) || 0), 0)
    + generalAttachments.reduce((s, a) => s + (parseInt(a.pages) || 0), 0);

  const completedExhibits = exhibits.filter(e => pennyDrafts?.['ex' + e.label + 'Final']?.source || e.finalVersion?.source || e.referenceOnly).length;

  return (
    <div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy-900)', margin: 0 }}>Contract Assembly</h2>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#1e3a8a', color: '#fff' }}>🏛 CALIFORNIA STATE</span>
          </div>
          <p style={{ fontSize: 13, color: 'var(--slate-500)', margin: 0 }}>
            {completedExhibits} of {exhibits.length} exhibits finalized · {totalPages > 0 ? totalPages + ' total pages' : 'No pages counted yet'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {exhibits.length === 0 && (
            <button className="btn btn-gold btn-sm" onClick={initializeDefaults}>
              ✦ Initialize Standard Exhibits
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={addExhibit}>
            <Icons.plus /> Add Exhibit
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowExportModal(true)}>
            📤 Export
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {exhibits.length > 0 && (
        <div style={{ height: 4, background: 'var(--slate-100)', borderRadius: 99, marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: 'linear-gradient(90deg, #c49922, #e8c04a)', borderRadius: 99, width: (completedExhibits / Math.max(exhibits.length, 1) * 100) + '%', transition: 'width .4s' }} />
        </div>
      )}

      {/* California forms reference */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'STD 215', sub: 'Approval/Routing Form', tab: 'std215', color: '#1e3a8a' },
          { label: 'STD 213', sub: 'Standard Agreement Face Sheet', tab: 'std213', color: '#1e3a8a' },
        ].map(form => (
          <div key={form.label} onClick={() => setTab && setTab(form.tab)} style={{
            padding: '12px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0',
            background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
            transition: 'all .15s',
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#c49922'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: form.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#e8c04a', flexShrink: 0 }}>CA</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-900)' }}>{form.label}</div>
              <div style={{ fontSize: 11, color: 'var(--slate-400)' }}>{form.sub} → Click to open</div>
            </div>
          </div>
        ))}
      </div>

      {/* Exhibits */}
      {exhibits.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⊞</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 6 }}>No exhibits yet</div>
          <div style={{ fontSize: 13, color: 'var(--slate-400)', marginBottom: 20 }}>
            Use standard exhibits (A=SOW, B=Budget, C=General Terms, D=Special Terms) or add your own.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button className="btn btn-gold" onClick={initializeDefaults}>✦ Initialize Standard Exhibits</button>
            <button className="btn btn-ghost" onClick={addExhibit}><Icons.plus /> Add Custom Exhibit</button>
          </div>
        </div>
      ) : (
        <>
          {exhibits.map((ex, idx) => (
            <ExhibitCard
              key={ex.id}
              ex={ex}
              isFirst={idx === 0}
              isLast={idx === exhibits.length - 1}
              onUpdate={updateExhibit}
              onRemove={removeExhibit}
              onMoveUp={() => moveExhibit(idx, -1)}
              onMoveDown={() => moveExhibit(idx, 1)}
              onAddAttachment={addAttachment}
              onUpdateAttachment={updateAttachment}
              onRemoveAttachment={removeAttachment}
              onUploadAttachment={uploadAttachment}
              onPullFromPenny={pullFromPenny}
              onSyncToPenny={syncToPenny}
              pennyDrafts={pennyDrafts}
            />
          ))}

          {/* Cost Sheet / Bid Sheet — from Penny tab */}
          {(() => {
            const cs = pennyDrafts?.costSheet;
            const csTable = pennyDrafts?.costSheetTable;
            const csMode = pennyDrafts?.costSheetMode || 'upload';
            const csStatus = pennyDrafts?.costSheetStatus || 'draft';
            const hasCs = !!(cs?.fileName || (csTable?.rows?.length > 0));
            if (!hasCs) return null;
            const statusColors = {
              draft: { color: 'var(--slate-500)', bg: 'var(--slate-50)', border: 'var(--slate-200)' },
              ready_for_review: { color: 'var(--blue-600)', bg: 'var(--blue-50)', border: 'var(--blue-200)' },
              in_review: { color: 'var(--purple-600)', bg: 'var(--purple-50)', border: 'var(--purple-200)' },
              approved: { color: 'var(--green-600)', bg: 'var(--green-50)', border: 'var(--green-200)' },
              needs_revision: { color: 'var(--orange-600)', bg: 'var(--orange-50)', border: 'var(--orange-200)' },
            };
            const sc = statusColors[csStatus] || statusColors.draft;
            const statusLabels = { draft: 'Draft', ready_for_review: 'Ready for Review', in_review: 'In Review', approved: 'Approved', needs_revision: 'Needs Revision' };
            return (
              <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid ' + sc.border, padding: '14px 16px', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 15 }}>📊</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-900)' }}>
                        Cost Sheet / Bid Sheet
                        <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: sc.bg, color: sc.color, border: '1px solid ' + sc.border }}>{statusLabels[csStatus]}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 2 }}>
                        {csMode === 'upload' ? (cs?.fileName || 'PDF uploaded') : 'Built in table builder · ' + (csTable?.rows?.length || 0) + ' rows'}
                        {' · Placement: ' + (pennyDrafts?.costSheetPlacement === 'exhibit_b1' ? 'Exhibit B, Attachment 1' : 'General Attachment')}
                      </div>
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => setTab('draft')} style={{ fontSize: 11 }}>
                    Edit in Penny →
                  </button>
                </div>
              </div>
            );
          })()}

          {/* General Attachments */}
          <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #e2e8f0', padding: '14px 16px', marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-900)' }}>General Attachments</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => generalFileRef.current?.click()}
                  style={{ padding: '5px 12px', borderRadius: 7, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 12, cursor: 'pointer' }}>
                  ↑ Upload
                </button>
                <button onClick={addGeneralAttachment}
                  style={{ padding: '5px 12px', borderRadius: 7, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 12, cursor: 'pointer' }}>
                  + Add
                </button>
              </div>
              <input ref={generalFileRef} type="file" accept=".pdf" style={{ display: 'none' }}
                onChange={e => e.target.files[0] && uploadGeneralAttachment(e.target.files[0])} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--slate-400)', marginBottom: 10, fontStyle: 'italic' }}>
              Documents not tied to a specific exhibit — DVBE forms, SB certifications, insurance certificates, etc.
            </div>
            {generalAttachments.length === 0 ? (
              <div style={{ fontSize: 12, color: '#cbd5e1', fontStyle: 'italic' }}>No general attachments yet</div>
            ) : (
              generalAttachments.map((att, i) => (
                <AttachmentRow
                  key={att.id} att={att} index={i}
                  onUpdate={updated => updateGeneralAttachment(att.id, updated)}
                  onRemove={() => removeGeneralAttachment(att.id)}
                  onUpload={(file) => uploadGeneralAttachment(file)}
                />
              ))
            )}
          </div>
        </>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          contract={contract}
          exhibits={exhibits}
          generalAttachments={generalAttachments}
          profile={profile}
          s215={s215}
          updateC={updateC}
          onClose={() => setShowExportModal(false)}
          toast={toast}
        />
      )}
    </div>
  );
}

// ── Export Modal ──────────────────────────────────────────────────────────────
function ExportModal({ contract, exhibits, generalAttachments, profile, updateC, onClose, toast, s215 }) {
  const presets = contract.pennyDrafts?.exportPresets || [];
  const [mode, setMode] = useState(presets.length > 0 ? 'presets' : 'new');
  const [presetName, setPresetName] = useState('');
  const [selectedDocs, setSelectedDocs] = useState([]); // ordered array of doc ids
  const [orderedDocs, setOrderedDocs] = useState([]); // ordered list after selection
  const [step, setStep] = useState('select'); // 'select' | 'order'
  const [exporting, setExporting] = useState(null); // preset id being exported
  const [dragIdx, setDragIdx] = useState(null);

  const availableDocs = [
    { id: 'std215', label: 'STD 215 — Approval/Routing Form', type: 'ca_form', icon: '📋' },
    { id: 'std213', label: 'STD 213 — Standard Agreement', type: 'ca_form', icon: '📄' },
    { id: 'std215_signed', label: 'STD 215 — Signed / Executed', type: 'signed_doc', icon: '✅', hasFinal: !!contract.s215?.signedDoc },
    { id: 'std213_signed', label: 'STD 213 — Signed / Executed', type: 'signed_doc', icon: '✅', hasFinal: !!contract.std213?.signedDoc },
    ...exhibits.filter(e => !e.referenceOnly).flatMap(e => [
      {
        id: 'exhibit_' + e.id, label: 'Exhibit ' + e.label + ' — ' + (e.name || 'Unnamed'), type: 'exhibit',
        exhibitId: e.id, hasFinal: !!(contract.pennyDrafts?.['ex' + e.label + 'Final']?.source || e.finalVersion?.source), icon: '📎',
      },
      // Nested attachments under this exhibit
      ...(e.attachments || []).map((att, ai) => ({
        id: 'exatt_' + e.id + '_' + att.id,
        label: 'Exhibit ' + e.label + ' — Attachment ' + (ai + 1) + (att.name ? ': ' + att.name : ''),
        type: 'ex_attachment', exhibitId: e.id, attId: att.id,
        hasFinal: !!(att.base64 || att.storagePath), icon: '📎',
      })),
    ]),
    { id: 'exhibit_c', label: 'Exhibit C — General Terms and Conditions (GTC)', type: 'reference', icon: '📎' },
    ...(contract.pennyDrafts?.costSheet?.fileName || contract.pennyDrafts?.costSheetTable?.rows?.length > 0 ? [{
      id: 'cost_sheet',
      label: 'Cost Sheet / Bid Sheet' + (contract.pennyDrafts?.costSheetPlacement === 'exhibit_b1' ? ' (Exhibit B, Attachment 1)' : ' (General Attachment)'),
      type: 'cost_sheet', icon: '📊',
      hasFinal: !!(contract.pennyDrafts?.costSheet?.fileName || contract.pennyDrafts?.costSheetTable?.rows?.length > 0),
    }] : []),
    ...generalAttachments.map((a, i) => ({
      id: 'gatt_' + a.id, label: 'General Attachment ' + (i + 1) + (a.name ? ' — ' + a.name : ''),
      type: 'attachment', attId: a.id, hasFinal: !!(a.base64 || a.storagePath), icon: '📎',
    })),
  ];

  function toggleDoc(docId) {
    setSelectedDocs(prev =>
      prev.includes(docId) ? prev.filter(d => d !== docId) : [...prev, docId]
    );
  }

  function goToOrder() {
    if (selectedDocs.length === 0) return;
    setOrderedDocs(selectedDocs.map(id => availableDocs.find(d => d.id === id)).filter(Boolean));
    setStep('order');
  }

  function moveDoc(from, to) {
    const arr = [...orderedDocs];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    setOrderedDocs(arr);
  }

  function savePreset() {
    if (!presetName.trim() || orderedDocs.length === 0) return;
    const newPreset = {
      id: uid(),
      name: presetName.trim(),
      docs: orderedDocs.map(d => d.id),
      docLabels: orderedDocs.map(d => d.label),
      createdAt: today(),
    };
    const existing = contract.pennyDrafts?.exportPresets || [];
    updateC({ pennyDrafts: { exportPresets: [...existing, newPreset] } });
    toast.success('Preset "' + presetName + '" saved');
    setPresetName(''); setSelectedDocs([]); setOrderedDocs([]); setStep('select'); setMode('presets');
  }

  function deletePreset(id) {
    const existing = contract.pennyDrafts?.exportPresets || [];
    updateC({ pennyDrafts: { exportPresets: existing.filter(p => p.id !== id) } });
  }

  async function getFileBlob(fileObj) {
    if (fileObj.base64) {
      const bytes = atob(fileObj.base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      return new Blob([arr], { type: 'application/pdf' });
    } else if (fileObj.storagePath) {
      const supabase = (await import('../../utils/supabaseClient.js')).default;
      const { data } = await supabase.storage.from('exhibit-files').download(fileObj.storagePath);
      return data;
    }
    return null;
  }

  async function runExport(preset, format) {
    setExporting(preset.id);
    const docIds = preset.docs || [];
    const baseName = (contract.title || 'Contract').replace(/[^a-zA-Z0-9]/g, '_');
    let downloaded = 0;

    toast.success('Preparing ' + format + ' export — ' + docIds.length + ' document(s)...');

    // Collect blobs for ZIP / Single PDF
    const collected = []; // { name, blob }
    const caForms = []; // std215/std213 — always open as print windows

    for (let i = 0; i < docIds.length; i++) {
      const docId = docIds[i];
      const num = String(i + 1).padStart(2, '0');
      const doc = availableDocs.find(d => d.id === docId);
      const label = (preset.docLabels?.[i] || doc?.label || docId).replace(/[^a-zA-Z0-9\s\-]/g, '').trim();
      const filename = num + '_' + label.replace(/\s+/g, '_') + '.pdf';

      try {
        if (docId === 'std215_signed') {
          const signedDoc = contract.s215?.signedDoc;
          if (signedDoc?.base64) {
            const blob = await getFileBlob(signedDoc);
            if (blob) { collected.push({ name: filename, blob }); downloaded++; }
            else toast.warn('STD 215 signed doc could not be loaded');
          } else {
            toast.warn('No signed STD 215 uploaded yet — upload one in the STD 215 tab');
          }
        } else if (docId === 'std213_signed') {
          const signedDoc = contract.std213?.signedDoc;
          if (signedDoc?.base64) {
            const blob = await getFileBlob(signedDoc);
            if (blob) { collected.push({ name: filename, blob }); downloaded++; }
            else toast.warn('STD 213 signed doc could not be loaded');
          } else {
            toast.warn('No signed STD 213 uploaded yet — upload one in the STD 213 tab');
          }
        } else if (docId === 'std215' || docId === 'std213') {
          caForms.push({ docId, label, filename });
        } else if (docId.startsWith('exhibit_') && docId !== 'exhibit_c') {
          const exId = docId.replace('exhibit_', '');
          const ex = exhibits.find(e => e.id === exId);
          const pennyFinal = contract.pennyDrafts?.['ex' + (ex?.label || '') + 'Final'];
          const finalToUse = pennyFinal || ex?.finalVersion;
          if (finalToUse) {
            const blob = await getFileBlob(finalToUse);
            if (blob) { collected.push({ name: filename, blob }); downloaded++; }
            else toast.warn('Exhibit ' + (ex?.label || '') + ' — could not load file');
          } else {
            toast.warn('Exhibit ' + (ex?.label || '') + ' has no final version — upload one in the Penny tab');
          }
        } else if (docId.startsWith('exatt_')) {
          // Nested exhibit attachment
          const parts = docId.replace('exatt_', '').split('_');
          const exId = parts[0];
          const attId = parts.slice(1).join('_');
          const ex = exhibits.find(e => e.id === exId);
          const att = (ex?.attachments || []).find(a => a.id === attId);
          if (att && (att.base64 || att.storagePath)) {
            const blob = await getFileBlob(att);
            if (blob) { collected.push({ name: filename, blob }); downloaded++; }
            else toast.warn(label + ' — could not load file');
          } else {
            toast.warn(label + ' — no file uploaded yet');
          }
        } else if (docId.startsWith('gatt_')) {
          const attId = docId.replace('gatt_', '');
          const att = generalAttachments.find(a => a.id === attId);
          if (att && (att.base64 || att.storagePath)) {
            const blob = await getFileBlob(att);
            if (blob) { collected.push({ name: filename, blob }); downloaded++; }
            else toast.warn(label + ' — could not load file');
          } else {
            toast.warn(label + ' — no file uploaded yet');
          }
        }
      } catch (e) {
        console.error('Export error for', docId, e);
        toast.error('Failed: ' + label);
      }
    }

    // ── Open CA forms as print windows (always) ──
    for (const form of caForms) {
      const { exportSTD215, exportSTD213 } = await import('../../utils/pdfExport.js');
      if (form.docId === 'std215') exportSTD215(contract, s215, profile, exhibits);
      if (form.docId === 'std213') exportSTD213(contract, s215, profile, exhibits);
      downloaded++;
      await new Promise(r => setTimeout(r, 300));
    }

    if (format === 'individual') {
      // Download each file individually
      for (const item of collected) {
        const url = URL.createObjectURL(item.blob);
        const a = document.createElement('a'); a.href = url; a.download = item.name;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        await new Promise(r => setTimeout(r, 400));
      }
      toast.success('Export complete — ' + downloaded + ' document(s) downloaded');

    } else if (format === 'zip') {
      // Bundle into ZIP using JSZip
      if (collected.length > 0) {
        const JSZip = window.JSZip;
        if (!JSZip) { toast.error('JSZip not loaded — refresh and try again'); setExporting(null); return; }
        const zip = new JSZip();
        const folder = zip.folder(baseName + '_' + preset.name.replace(/\s+/g, '_'));
        for (const item of collected) folder.file(item.name, item.blob);
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement('a'); a.href = url;
        a.download = baseName + '_' + preset.name.replace(/[^a-zA-Z0-9]/g, '_') + '.zip';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('ZIP downloaded — ' + collected.length + ' file(s) bundled' + (caForms.length > 0 ? ' · CA forms opened in print windows' : ''));
      } else if (caForms.length > 0) {
        toast.success('CA forms opened in print windows for downloading');
      }

    } else if (format === 'single') {
      // Combine all into one PDF using jsPDF
      if (typeof window.jspdf === 'undefined') {
        const s = document.createElement('script');
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        await new Promise((res, rej) => { s.onload = res; s.onerror = rej; document.head.appendChild(s); });
      }
      const { jsPDF } = window.jspdf;
      if (!jsPDF) { toast.error('jsPDF not loaded'); setExporting(null); return; }

      if (collected.length > 0) {
        // Convert each PDF to images and combine — simple approach using FileReader + img
        const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
        let firstPage = true;

        for (const item of collected) {
          // Use PDF.js to render each page as canvas if available, else just add a text page
          try {
            const arrayBuffer = await item.blob.arrayBuffer();
            const uint8 = new Uint8Array(arrayBuffer);
            // Check if PDF.js is available
            if (window.pdfjsLib) {
              const pdfDoc = await window.pdfjsLib.getDocument({ data: uint8 }).promise;
              for (let p = 1; p <= pdfDoc.numPages; p++) {
                const page = await pdfDoc.getPage(p);
                const viewport = page.getViewport({ scale: 1.5 });
                const canvas = document.createElement('canvas');
                canvas.width = viewport.width; canvas.height = viewport.height;
                await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
                const imgData = canvas.toDataURL('image/jpeg', 0.92);
                if (!firstPage) pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, 0, 612, 792);
                firstPage = false;
              }
            } else {
              // Fallback: add a page with the filename
              if (!firstPage) pdf.addPage();
              pdf.setFontSize(12); pdf.setFont('helvetica', 'bold');
              pdf.text(item.name.replace('.pdf', ''), 306, 400, { align: 'center' });
              pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
              pdf.text('(Open this file separately — PDF rendering library not available)', 306, 420, { align: 'center' });
              firstPage = false;
            }
          } catch (e) {
            console.warn('Could not render PDF page:', e);
            if (!firstPage) pdf.addPage();
            pdf.setFontSize(10); pdf.text(item.name, 36, 400);
            firstPage = false;
          }
        }

        pdf.save(baseName + '_' + preset.name.replace(/[^a-zA-Z0-9]/g, '_') + '_Combined.pdf');
        if (caForms.length > 0) toast.success('Combined PDF downloaded · CA forms opened in print windows');
        else toast.success('Combined PDF downloaded — ' + collected.length + ' document(s)');
      } else if (caForms.length > 0) {
        toast.success('CA forms opened in print windows for downloading');
      }
    }

    setExporting(null);
  }



  const savedPresets = contract.pennyDrafts?.exportPresets || [];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,.25)' }}>

        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f1f3d' }}>📤 Export Contract Package</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Each document downloads individually in order</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>

          {/* Mode switcher */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[{ key: 'presets', label: '📋 My Presets' }, { key: 'new', label: '+ New Preset' }].map(m => (
              <button key={m.key} onClick={() => { setMode(m.key); setStep('select'); }} style={{
                padding: '7px 16px', borderRadius: 8, border: '1.5px solid',
                borderColor: mode === m.key ? '#c49922' : '#e2e8f0',
                background: mode === m.key ? '#fffbeb' : '#f8fafc',
                color: mode === m.key ? '#92400e' : '#64748b',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>{m.label}</button>
            ))}
          </div>

          {/* ── SAVED PRESETS ── */}
          {mode === 'presets' && (
            savedPresets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No presets yet</div>
                <div style={{ fontSize: 12 }}>Create a preset to save your preferred document selection and order.</div>
                <button onClick={() => setMode('new')} style={{ marginTop: 16, padding: '8px 20px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#c49922,#e8c04a)', color: '#0f1f3d', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  + Create First Preset
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {savedPresets.map(preset => (
                  <div key={preset.id} style={{ padding: '14px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f1f3d' }}>{preset.name}</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => deletePreset(preset.id)}
                          style={{ background: 'none', border: '1px solid #fecaca', borderRadius: 5, color: '#dc2626', fontSize: 11, padding: '3px 8px', cursor: 'pointer' }}>
                          Delete
                        </button>
                        {exporting === preset.id ? (
                          <span style={{ fontSize: 12, color: '#94a3b8', padding: '5px 10px' }}>⏳ Exporting...</span>
                        ) : (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => runExport(preset, 'individual')} disabled={!!exporting}
                              title="Download each document separately"
                              style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#374151', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                              📄 Individual
                            </button>
                            <button onClick={() => runExport(preset, 'zip')} disabled={!!exporting}
                              title="Download all as a single ZIP file"
                              style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#374151', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                              🗜 ZIP
                            </button>
                            <button onClick={() => runExport(preset, 'single')} disabled={!!exporting}
                              title="Combine all into one PDF"
                              style={{ padding: '5px 8px', borderRadius: 6, border: 'none', background: 'linear-gradient(135deg,#c49922,#e8c04a)', color: '#0f1f3d', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>
                              ⬇ Single PDF
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: preset.docLabels ? 6 : 0 }}>
                      {preset.docs.length} document{preset.docs.length !== 1 ? 's' : ''} · Created {preset.createdAt}
                    </div>
                    {preset.docLabels && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {preset.docLabels.map((label, i) => (
                          <div key={i} style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', minWidth: 18 }}>{i + 1}.</span>
                            {label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          )}

          {/* ── NEW PRESET — STEP 1: SELECT ── */}
          {mode === 'new' && step === 'select' && (
            <div>
              <input value={presetName} onChange={e => setPresetName(e.target.value)}
                placeholder="Preset name (e.g., DGS Routing Package)"
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 14 }} />
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '.05em', marginBottom: 8, textTransform: 'uppercase' }}>
                Select documents to include:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {availableDocs.map(doc => (
                  <label key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, border: '1.5px solid ' + (selectedDocs.includes(doc.id) ? '#c49922' : '#e2e8f0'), background: selectedDocs.includes(doc.id) ? '#fffbeb' : '#f8fafc', cursor: 'pointer' }}>
                    <input type="checkbox" checked={selectedDocs.includes(doc.id)}
                      onChange={() => toggleDoc(doc.id)} style={{ width: 15, height: 15, accentColor: '#c49922' }} />
                    <span style={{ fontSize: 13 }}>{doc.icon}</span>
                    <span style={{ fontSize: 13, color: '#374151', flex: 1 }}>{doc.label}</span>
                    {doc.hasFinal === false && <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 600 }}>⚠ No final</span>}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* ── NEW PRESET — STEP 2: ORDER ── */}
          {mode === 'new' && step === 'order' && (
            <div>
              <div style={{ padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, marginBottom: 14, fontSize: 12, color: '#1e40af' }}>
                ↕ Drag to reorder — documents will download in this order when you run the export.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {orderedDocs.map((doc, i) => (
                  <div key={doc.id}
                    draggable
                    onDragStart={() => setDragIdx(i)}
                    onDragOver={e => { e.preventDefault(); }}
                    onDrop={() => { if (dragIdx !== null && dragIdx !== i) { moveDoc(dragIdx, i); setDragIdx(null); } }}
                    onDragEnd={() => setDragIdx(null)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                      borderRadius: 8, border: '1.5px solid #e2e8f0', background: dragIdx === i ? '#f0fdf4' : '#fff',
                      cursor: 'grab', userSelect: 'none',
                      opacity: dragIdx === i ? 0.5 : 1,
                    }}>
                    <span style={{ fontSize: 14, color: '#cbd5e1' }}>⠿</span>
                    <span style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', minWidth: 20 }}>{i + 1}.</span>
                    <span style={{ fontSize: 13 }}>{doc.icon}</span>
                    <span style={{ fontSize: 13, color: '#374151', flex: 1 }}>{doc.label}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => i > 0 && moveDoc(i, i - 1)}
                        disabled={i === 0}
                        style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 6px', fontSize: 11, cursor: i === 0 ? 'not-allowed' : 'pointer', color: i === 0 ? '#cbd5e1' : '#64748b' }}>▲</button>
                      <button onClick={() => i < orderedDocs.length - 1 && moveDoc(i, i + 1)}
                        disabled={i === orderedDocs.length - 1}
                        style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 6px', fontSize: 11, cursor: i === orderedDocs.length - 1 ? 'not-allowed' : 'pointer', color: i === orderedDocs.length - 1 ? '#cbd5e1' : '#64748b' }}>▼</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
          {mode === 'new' && step === 'order' && (
            <button onClick={() => setStep('select')} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 13, cursor: 'pointer', color: '#64748b' }}>
              ← Back
            </button>
          )}
          {mode === 'new' && step === 'select' && (
            <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 13, cursor: 'pointer', color: '#64748b' }}>
              Cancel
            </button>
          )}
          <div style={{ flex: 1 }} />
          {mode === 'new' && step === 'select' && (
            <button onClick={goToOrder} disabled={selectedDocs.length === 0 || !presetName.trim()}
              style={{ padding: '9px 20px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 13, cursor: selectedDocs.length === 0 || !presetName.trim() ? 'not-allowed' : 'pointer', background: selectedDocs.length === 0 || !presetName.trim() ? '#e2e8f0' : 'linear-gradient(135deg,#0f1f3d,#1e3a8a)', color: selectedDocs.length === 0 || !presetName.trim() ? '#94a3b8' : '#fff' }}>
              Set Order → ({selectedDocs.length} selected)
            </button>
          )}
          {mode === 'new' && step === 'order' && (
            <button onClick={savePreset} disabled={!presetName.trim() || orderedDocs.length === 0}
              style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#c49922,#e8c04a)', color: '#0f1f3d', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              💾 Save Preset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
